import * as React from "react";
import { useState } from "react";
import {
  Show,
  TextField,
  ImageField,
  BooleanField,
  TopToolbar,
  EditButton,
  useDataProvider,
  useRedirect,
  useNotify,
  Button,
  ReferenceField,
  ArrayField,
  DataTable,
  NumberField,
  FunctionField,
  SelectField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  Confirm,
  usePermissions,
  useRecordContext,
  Title,
  LinearProgress,
  Link,
} from "react-admin";
import QueueIcon from "@mui/icons-material/Queue";
import PublishedWithChangesIcon from "@mui/icons-material/Done";

import { getTimeFromDate, shortWeekdayToLong } from "../utils/DateTimeUtils";
import { ROLE_TYPES } from "../enums/Roles";
import { AuditButton } from "../common/AuditButton";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { PAYMENT_METHODS } from "../enums/PaymentMethods";
import { TabbedShowLayout } from "../common/TabbedShowLayout";
import VideoField from "../common/VideoField";
import { COUNTRIES } from "../enums/Countries";
import { Instagram, Mail, WhatsApp, Phone } from "@mui/icons-material";

const RestaurantShowActions = ({ setShowConfirm, isLoading }) => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  const onCreateProductBulkClick = () => {
    window.location.replace(
      `/#/products-draft/bulk-create-draft?restaurant=${record.id}`
    );
  };

  const onApproveRestaurantClick = () => {
    setShowConfirm(true);
  };

  if (isLoading) return <LinearProgress />;

  return (
    <TopToolbar>
      <EditButton />
      {permissions === ROLE_TYPES.ADMIN && record?.onboardingCompleted && (
        <Button label={"Aprobar"} onClick={onApproveRestaurantClick}>
          <PublishedWithChangesIcon />
        </Button>
      )}
      {permissions === ROLE_TYPES.ADMIN && (
        <Button label="Crear productos" onClick={onCreateProductBulkClick}>
          <QueueIcon />
        </Button>
      )}
      {permissions === ROLE_TYPES.ADMIN && (
        <AuditButton resource="restaurants" />
      )}
    </TopToolbar>
  );
};

const RestaurantTitle = () => {
  const record = useRecordContext();
  return <Title title={`Restaurante "${record?.name}"`} />;
};

const RestaurantShow = ({
  setShowConfirm,
  setIsLoading,
  isLoading,
  showConfirm,
}) => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  const handleConfirm = () => {
    notify("Aprobando restaurante...");
    setIsLoading(true);
    dataProvider
      .approveRestaurant("restaurants", { id: record.id })
      .then((data) => {
        notify("Restaurante aprobado con éxito", {
          type: "success",
        });
        redirect(`/restaurants/${record.id}`);
      })
      .catch((error) => {
        notify("Ha ocurrido un error aprobando el restaurante", {
          type: "error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleDialogClose = () => {
    setShowConfirm(false);
  };

  const normalizeInstagram = (urlOrHandle = "") => {
    return (
      urlOrHandle
        .trim()
        // Remove protocol (http:// or https://) and optional "www."
        .replace(/^(https?:\/\/)?(www\.)?/, "")
        // Remove "instagram.com/" if present
        .replace(/^instagram\.com\//, "")
        // Remove a leading "@" if it exists
        .replace(/^@/, "")
        // Remove a trailing slash if present
        .replace(/\/$/, "")
    );
  };

  return (
    <>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Datos básicos">
          {permissions === ROLE_TYPES.ADMIN && (
            <TextField source="id" label="ID" />
          )}
          <TextField source="name" label="Nombre" />
          <TextField source="description" label="Descripción" />
          <TextField source="address" label="Dirección" />
          <ReferenceField
            source="district"
            reference="districts"
            link={false}
            label="Barrio"
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField source="location[1]" label="Latitud" />
          <TextField source="location[0]" label="Longitud" />
          <SelectField
            label="Tipo"
            source="type"
            choices={[
              { id: RESTAURANT_TYPES.REGULAR, name: "Regular" },
              { id: RESTAURANT_TYPES.GUIDE, name: "Guía" },
              { id: RESTAURANT_TYPES.PREGUIDE, name: "Pre-Guía" },
              { id: RESTAURANT_TYPES.DRAFT, name: "Borrador" },
            ]}
          />
          <ReferenceArrayField
            source="placeTypes"
            reference="place-types"
            label="Tipos de Lugar"
          >
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>

          <ReferenceArrayField
            source="placeTargets"
            reference="place-targets"
            label="Targets de Lugar"
          >
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>

          <FunctionField
            source="couponPaymentMethods"
            render={(record) =>
              `${record?.couponPaymentMethods
                ?.map((d) =>
                  d === PAYMENT_METHODS.CREDIT_CARD
                    ? "Tarjeta de Crédito"
                    : d === PAYMENT_METHODS.DEBIT_CARD
                      ? "Tarjeta de Débito"
                      : d === PAYMENT_METHODS.MERCADO_PAGO
                        ? "Mercado Pago"
                        : d === PAYMENT_METHODS.CASH
                          ? "Efectivo"
                          : ""
                )
                .join(", ")}`
            }
            label="Métodos de Pago (Cupones)"
          />

          {permissions === ROLE_TYPES.ADMIN && (
            <TextField source="googlePlaceId" label="Google Place ID" />
          )}

          <BooleanField source="active" label="Activo" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Datos de contacto">
          <FunctionField
            source="contactData.email"
            label="Email"
            render={(record) => {
              const rawEmail = record?.contactData?.email;
              if (!rawEmail) return null;

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>{rawEmail}</span>
                  <Link
                    to={`mailto:${rawEmail}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Mail fontSize="small" />
                  </Link>
                </div>
              );
            }}
          />
          <FunctionField
            source="contactData.phone"
            label="Teléfono"
            render={(record) => {
              const rawPhone = record?.contactData?.phone;
              if (!rawPhone) return null;

              const normalizedPhone = rawPhone.replace(/\D+/g, "");
              if (!normalizedPhone) return null;

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>{rawPhone}</span>
                  <Link
                    to={`https://wa.me/${normalizedPhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsApp fontSize="small" />
                  </Link>
                  <Link
                    to={`tel:${rawPhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Phone fontSize="small" />
                  </Link>
                </div>
              );
            }}
          />
          <FunctionField
            source="contactData.instagram"
            label="Instagram"
            render={(record) => {
              const rawInstagram = record?.contactData?.instagram;
              if (!rawInstagram) return null;

              const username = normalizeInstagram(rawInstagram);
              if (!username) return null;

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>{username}</span>
                  <Link
                    to={`https://www.instagram.com/${username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram fontSize="small" />
                  </Link>
                </div>
              );
            }}
          />
        </TabbedShowLayout.Tab>
        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedShowLayout.Tab label="Productos">
            <ArrayField source="products" label="Productos">
              <DataTable
                storeKey="restaurantDraft.products"
                rowClick={false}
                bulkActionButtons={false}
              >
                <DataTable.Col source="id" label="Nombre">
                  <ReferenceField
                    source="id"
                    reference="products"
                    label="Nombre"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </DataTable.Col>
                <DataTable.Col source="description" label="Descripción" />
                <DataTable.Col source="price" label="Precio" />
                <DataTable.Col source="status" label="Estado" />
                <DataTable.Col source="type" label="Tipo" />
                <DataTable.Col source="category" label="Categoría" />
              </DataTable>
            </ArrayField>
          </TabbedShowLayout.Tab>
        )}
        <TabbedShowLayout.Tab label="Días y Horarios">
          <ArrayField source="timeTable" label="">
            <DataTable
              storeKey="restaurantDraft.timeTable"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="days" label="Días">
                <FunctionField
                  source="days"
                  render={(record) =>
                    `${record.days.map((d) => shortWeekdayToLong(d)).join(", ")}`
                  }
                  label="Días"
                />
              </DataTable.Col>
              <DataTable.Col source="times" label="">
                <ArrayField source="times" label="">
                  <DataTable
                    storeKey="restaurantDraft.times"
                    rowClick={false}
                    bulkActionButtons={false}
                  >
                    <DataTable.Col source="opening" label="Apertura">
                      <FunctionField
                        source="opening"
                        render={(timeRecord) =>
                          getTimeFromDate(timeRecord.opening, record.country)
                        }
                        label="Apertura"
                      />
                    </DataTable.Col>
                    <DataTable.Col source="closing" label="Cierre">
                      <FunctionField
                        source="closing"
                        render={(timeRecord) =>
                          getTimeFromDate(timeRecord.closing, record.country)
                        }
                        label="Cierre"
                      />
                    </DataTable.Col>
                  </DataTable>
                </ArrayField>
              </DataTable.Col>
            </DataTable>
          </ArrayField>
        </TabbedShowLayout.Tab>

        {permissions === ROLE_TYPES.ADMIN &&
          record?.country &&
          COUNTRIES[record.country] === COUNTRIES.AR && (
            <TabbedShowLayout.Tab label="Facturación">
              <TextField label="Email" source="billingData.email" />
              <TextField label="CUIT" source="billingData.cuit" />
              <TextField label="Razón Social" source="billingData.name" />
              <TextField
                label="Domicilio fiscal"
                source="billingData.address"
              />
              <TextField label="Tipo documento" source="billingData.docType" />
              <TextField label="Provincia" source="billingData.province" />
              <TextField
                label="Condición frente al IVA"
                source="billingData.taxCondition"
              />
            </TabbedShowLayout.Tab>
          )}

        <TabbedShowLayout.Tab label="Compras">
          <FunctionField
            source="purchasePaymentMethods"
            render={(record) =>
              `${record?.purchasePaymentMethods
                ?.map((d) =>
                  d === PAYMENT_METHODS.CREDIT_CARD
                    ? "Tarjeta de Crédito"
                    : d === PAYMENT_METHODS.DEBIT_CARD
                      ? "Tarjeta de Débito"
                      : d === PAYMENT_METHODS.MERCADO_PAGO
                        ? "Mercado Pago"
                        : ""
                )
                .join(", ")}`
            }
            label="Métodos de Pago (Compras)"
          />
          {permissions === ROLE_TYPES.ADMIN && (
            <NumberField
              source="purchaseSplitPercentage"
              label="% Fee de Morfy"
              options={{
                style: "percent",
                maximumFractionDigits: 2,
              }}
            />
          )}
          <BooleanField source="enablePurchases" label="Compras Habilitadas" />
        </TabbedShowLayout.Tab>
        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedShowLayout.Tab label="Premios">
            <ArrayField source="rewards" label="">
              <DataTable
                storeKey="restaurantDraft.rewards"
                rowClick={false}
                bulkActionButtons={false}
              >
                <DataTable.Col source="product._id" label="Producto">
                  <ReferenceField
                    source="product._id"
                    reference="products"
                    label="Producto"
                    link={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </DataTable.Col>
                <DataTable.NumberCol
                  source="necessaryRedemptions"
                  label="Canjes Necesarios"
                />
                <DataTable.Col source="active" label="Activo">
                  <BooleanField source="active" label="Activo" />
                </DataTable.Col>
              </DataTable>
            </ArrayField>
          </TabbedShowLayout.Tab>
        )}
        <TabbedShowLayout.Tab label="Imágenes">
          <ArrayField source="coverImages" label="Imágenes de Cover">
            <DataTable
              storeKey="restaurantDraft.coverImages"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="url" label="">
                <ImageField source="url" label="" />
              </DataTable.Col>
            </DataTable>
          </ArrayField>
          <ImageField source="profileImage" label="Imagen de Perfil" />
          <ImageField source="mapImage" label="Imagen de Mapa" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Videos">
          <ArrayField source="videos" label="Videos">
            <DataTable
              storeKey="restaurantDraft.videos"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="name" label="Nombre" />
              <DataTable.Col label="Video">
                <VideoField label="Video" />
              </DataTable.Col>
              <DataTable.Col source="thumbnail" label="Miniatura">
                <ImageField source="thumbnail" label="Miniatura" />
              </DataTable.Col>
              <DataTable.Col source="active" label="Activo">
                <BooleanField source="active" label="Activo" />
              </DataTable.Col>
            </DataTable>
          </ArrayField>
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>

      <Confirm
        isOpen={showConfirm}
        loading={isLoading}
        title="¿Estás seguro de aprobar este restaurante?"
        content="Esto cambiará su estado a Aprobado, pero aún no estará visible en la plataforma hasta que lo actives manualmente."
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

const RestaurantShowContainer = () => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Show
      title={<RestaurantTitle />}
      actions={
        <RestaurantShowActions
          setShowConfirm={setShowConfirm}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          showConfirm={showConfirm}
        />
      }
    >
      <RestaurantShow
        setShowConfirm={setShowConfirm}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        showConfirm={showConfirm}
      />
    </Show>
  );
};

export default RestaurantShowContainer;
